import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Head from "../components/Head";
import Foot from "../components/Foot";
import NotFound from "./NotFound";

const apiUrl = process.env.REACT_APP_API_URL;

function setMetaTag(name, content) {
  if (!content) return;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function getYouTubeEmbedUrl(url) {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

function CoursePage() {
  const { slug } = useParams();
  const [coursePage, setCoursePage] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchCoursePage = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const response = await axios.get(`${apiUrl}/api/public/course-pages/${slug}`);
        if (response.data.success) {
          setCoursePage(response.data.coursePage);
          setGallery(response.data.gallery || []);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCoursePage();
  }, [slug]);

  useEffect(() => {
    if (!coursePage) return;
    document.title = coursePage.meta_title || coursePage.title;
    setMetaTag("description", coursePage.meta_description);
    setMetaTag("keywords", coursePage.meta_keywords);
  }, [coursePage]);

  if (loading) {
    return (
      <>
        <Head />
        <div className="container text-center" style={{ padding: "120px 0" }}>
          <p>Loading...</p>
        </div>
        <Foot />
      </>
    );
  }

  if (notFound || !coursePage) {
    return <NotFound />;
  }

  const titleStyle = {
    fontSize: `${coursePage.title_font_size || 36}px`,
    fontWeight: coursePage.title_bold ? 700 : 400,
  };

  const CallButton = (
    <a href="tel:+91 90486 48777" className="common-btn">
      <span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={19}
          height={19}
          viewBox="0 0 19 19"
          fill="none"
        >
          <g clipPath="url(#clip0_251_1595)">
            <path
              d="M17.1462 12.3433C16.1022 12.3433 15.0771 12.18 14.1057 11.859C13.6297 11.6966 13.0445 11.8456 12.754 12.144L10.8366 13.5914C8.61296 12.4044 7.24324 11.0351 6.07247 8.8282L7.47732 6.96076C7.84231 6.59626 7.97322 6.06381 7.81637 5.56422C7.49397 4.58769 7.3302 3.56307 7.3302 2.51859C7.33024 1.76406 6.71639 1.15021 5.9619 1.15021H2.83025C2.07576 1.15021 1.46191 1.76406 1.46191 2.51855C1.46191 11.167 8.49781 18.2028 17.1462 18.2028C17.9007 18.2028 18.5146 17.589 18.5146 16.8345V13.7116C18.5145 12.9572 17.9007 12.3433 17.1462 12.3433Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0_251_1595">
              <rect
                width={18}
                height={18}
                fill="white"
                transform="translate(0.988281 0.619019)"
              />
            </clipPath>
          </defs>
        </svg>
      </span>
      <span className="mt-1" style={{ color: "white" }}>
        Call: +91 90486 48777
      </span>
    </a>
  );

  return (
    <>
      <Head />
      {/* Start Hero */}
      <section className="common-page-hero">
        <img
          src="assets/img/bg/common-bg.png"
          className="common-hero-bg ak-bg"
          alt="..."
        />
        <div className="common-hero-content">
          <div className="common-hero-info">
            <div className="ak-section-heading ak-style-1 animation-title-content animation-style3">
              <p>&nbsp;</p>
              <h2 className="ak-section-title animation-title" style={titleStyle}>
                {coursePage.title}
              </h2>
            </div>
            <p className="back-btn">
              <a href="/">Home</a> / {coursePage.menu_category} / {coursePage.title}
            </p>
          </div>
          <a href="tel:+91 90486 48777" className="phone-number">
            {coursePage.menu_category}
          </a>
        </div>
      </section>
      {/* End Hero */}

      {/* Start course Details */}
      <div className="ak-height-60 ak-height-lg-50" />
      <div className="atd">
        <div className="container">
          <div className="atd-content">
            <div className="atd-image-section">
              {coursePage.cover_image && (
                <img src={`${apiUrl}/${coursePage.cover_image}`} alt="course cover" />
              )}
              <div className="atd-email-vertical">
                <a href="#">{coursePage.menu_category.toUpperCase()}</a>
              </div>
            </div>
            <div className="atd-section">
              <h3 className="atd-title" style={titleStyle}>{coursePage.title}</h3>
              <p className="atd-shot-title">{coursePage.menu_category}</p>
              <br />
              {coursePage.subtitle && (
                <h5 className="atd-subtitle">{coursePage.subtitle}</h5>
              )}
              {coursePage.intro && (
                <div
                  className="atd-desp"
                  dangerouslySetInnerHTML={{ __html: coursePage.intro }}
                />
              )}
              <div className="ak-height-50 ak-height-lg-30" />
              {CallButton}
            </div>
          </div>
        </div>
      </div>
      {/* End course Details */}

      {/* Pgrm Details */}
      <div className="ak-height-125 ak-height-lg-80" />
      <div className="container-fluid px-md-0">
        <div className="contact-form-content">
          {coursePage.secondary_image && (
            <div className="contact-form-img image-hov-one">
              <img src={`${apiUrl}/${coursePage.secondary_image}`} alt="..." />
            </div>
          )}
          <div className="contact-form">
            <div dangerouslySetInnerHTML={{ __html: coursePage.body || "" }} />
          </div>
        </div>
      </div>
      {/* End pgrm details */}

      {gallery.length > 0 && (
        <>
          <div className="ak-height-65 ak-height-lg-50" />
          <div className="container">
            <div className="ak-section-heading ak-style-1 text-center mb-4">
              <h3 className="ak-section-title">Gallery</h3>
            </div>
            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
              {gallery.map((item) => (
                <div className="col" key={item.id}>
                  <div className="image-card h-100 bg-white shadow-lg rounded-lg overflow-hidden">
                    {item.type === "image" ? (
                      <img
                        src={`${apiUrl}/${item.media_path}`}
                        alt={item.caption || coursePage.title}
                        className="w-full h-64 object-cover"
                      />
                    ) : (
                      <div className="ratio ratio-16x9">
                        {getYouTubeEmbedUrl(item.video_url) ? (
                          <iframe
                            src={getYouTubeEmbedUrl(item.video_url)}
                            title={item.caption || "Course video"}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <a href={item.video_url} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center justify-content-center bg-dark text-white">
                            Watch Video
                          </a>
                        )}
                      </div>
                    )}
                    {item.caption && (
                      <div className="p-3">
                        <p className="mb-0 text-14">{item.caption}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="ak-height-20 ak-height-lg-30" />
      <Foot />
    </>
  );
}

export default CoursePage;
